import React from 'react';
import { 
  XMarkIcon, 
  EnvelopeIcon, 
  ClockIcon, 
  UserIcon, 
  PaperClipIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Email } from '../types/Email';

interface EmailDetailModalProps {
  email: Email;
  onClose: () => void;
}

const EmailDetailModal: React.FC<EmailDetailModalProps> = ({ email, onClose }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'draft':
        return <DocumentTextIcon className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <ExclamationCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <EnvelopeIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatEmailList = (emails: string[]) => {
    return emails.length > 0 ? emails.join(', ') : 'None';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Email Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Email Information */}
            <div className="space-y-6">
              {/* Status and Subject */}
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(email.status)}`}>
                    {getStatusIcon(email.status)}
                    <span className="ml-2 capitalize">{email.status}</span>
                  </span>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Subject</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {email.subject}
                  </p>
                </div>
              </div>

              {/* Email Addresses */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">From</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {email.fromName} &lt;{email.fromEmail}&gt;
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">To</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatEmailList(email.toEmails)}
                  </p>
                </div>

                {email.ccEmails.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">CC</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatEmailList(email.ccEmails)}
                    </p>
                  </div>
                )}

                {email.bccEmails.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">BCC</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatEmailList(email.bccEmails)}
                    </p>
                  </div>
                )}
              </div>

              {/* Attachments */}
              {email.attachments && email.attachments.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Attachments</label>
                  <div className="mt-1 space-y-2">
                    {email.attachments.map((attachment) => (
                      <div key={attachment.id} className="flex items-center space-x-3 p-2 bg-gray-50 rounded-md">
                        <PaperClipIcon className="w-4 h-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {attachment.originalFilename}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(attachment.fileSize)} • {attachment.mimeType}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Content and Metadata */}
            <div className="space-y-6">
              {/* Email Body */}
              <div>
                <label className="text-sm font-medium text-gray-700">Message</label>
                <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-4 rounded-md max-h-96 overflow-y-auto">
                  {email.htmlBody ? (
                    <div 
                      dangerouslySetInnerHTML={{ __html: email.htmlBody }}
                      className="prose prose-sm max-w-none"
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans">{email.body}</pre>
                  )}
                </div>
              </div>

              {/* Timing Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Timing Information</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created</label>
                    <p className="text-sm text-gray-900">
                      {new Date(email.createdAt).toLocaleString()}
                    </p>
                  </div>
                  
                  {email.sentAt && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</label>
                      <p className="text-sm text-gray-900">
                        {new Date(email.sentAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</label>
                    <p className="text-sm text-gray-900">
                      {new Date(email.updatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Creator Information */}
              {(email.createdByFirstName || email.createdByLastName) && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Created By</h4>
                  <p className="text-sm text-gray-900">
                    {email.createdByFirstName} {email.createdByLastName}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDetailModal;
